import React from 'react';
import type {ObjectLocksOverlayProps} from '@/types/components/objectLocks.types';
import {graphLocalToClient} from '@/utils/diagram/graphClientPoint';
import {colorForUserId} from '@/utils/diagram/userColor';
import './ObjectLocksOverlay.css';

type LockBox = {left: number; top: number; width: number; height: number};

type GraphLike = {
	getCellById?: (id: string) => {getBBox?: () => {x: number; y: number; width: number; height: number}} | null;
	findViewByCell?: (cell: unknown) => {container?: Element} | null;
	localToClient?: (x: number, y: number) => {x?: number; y?: number} | null;
	coord?: {
		localToClientPoint?: (x: number, y: number) => {x?: number; y?: number} | null;
	};
	on?: (name: string, handler: () => void) => void;
	off?: (name: string, handler: () => void) => void;
};

function measureLockBox(graph: GraphLike, cell: unknown, origin: DOMRect): LockBox | null {
	const view = graph.findViewByCell?.(cell);
	const rect = view?.container?.getBoundingClientRect?.();
	if (rect && (rect.width > 0 || rect.height > 0)) {
		return {
			left: rect.left - origin.left,
			top: rect.top - origin.top,
			width: rect.width,
			height: rect.height,
		};
	}
	const bbox = (cell as {getBBox?: () => {x: number; y: number; width: number; height: number} | null})?.getBBox?.();
	if (!bbox) return null;
	const tl = graphLocalToClient(graph, bbox.x, bbox.y);
	const br = graphLocalToClient(graph, bbox.x + bbox.width, bbox.y + bbox.height);
	if (!tl || !br) return null;
	return {
		left: Math.min(tl.x, br.x) - origin.left,
		top: Math.min(tl.y, br.y) - origin.top,
		width: Math.abs(br.x - tl.x),
		height: Math.abs(br.y - tl.y),
	};
}

const ObjectLocksOverlay: React.FC<ObjectLocksOverlayProps> = ({
	locks,
	localSessionId,
	activeViewId,
	containerRef,
	modelerRef,
	modelerReady,
	viewReady,
}) => {
	const [, setTransformTick] = React.useState(0);

	React.useEffect(() => {
		if (!modelerReady || !viewReady) return;
		const graph = modelerRef.current?.getGraph?.() as GraphLike | undefined;
		const container = containerRef.current;
		const bump = () => setTransformTick(tick => tick + 1);
		graph?.on?.('scale', bump);
		graph?.on?.('translate', bump);
		graph?.on?.('resize', bump);
		graph?.on?.('node:change:position', bump);
		graph?.on?.('node:change:size', bump);
		graph?.on?.('cell:change:position', bump);
		graph?.on?.('cell:changed', bump);
		graph?.on?.('batch:stop', bump);
		container?.addEventListener('wheel', bump, {passive: true});
		const onPointerMove = (event: PointerEvent) => {
			if (event.buttons) bump();
		};
		container?.addEventListener('pointermove', onPointerMove);
		window.addEventListener('resize', bump);
		return () => {
			graph?.off?.('scale', bump);
			graph?.off?.('translate', bump);
			graph?.off?.('resize', bump);
			graph?.off?.('node:change:position', bump);
			graph?.off?.('node:change:size', bump);
			graph?.off?.('cell:change:position', bump);
			graph?.off?.('cell:changed', bump);
			graph?.off?.('batch:stop', bump);
			container?.removeEventListener('wheel', bump);
			container?.removeEventListener('pointermove', onPointerMove);
			window.removeEventListener('resize', bump);
		};
	}, [modelerReady, viewReady, modelerRef, containerRef]);

	const remoteLocks = React.useMemo(
		() => locks.filter(lock => !(localSessionId && lock.clientSessionId === localSessionId)),
		[locks, localSessionId]
	);

	React.useEffect(() => {
		if (!modelerReady || !viewReady || remoteLocks.length === 0) return;
		const lastSig = {current: ''};
		let frame = 0;
		const sample = () => {
			const graph = modelerRef.current?.getGraph?.() as GraphLike | undefined;
			const origin = containerRef.current?.getBoundingClientRect();
			if (graph && origin) {
				const sig = remoteLocks
					.map(lock => {
						const cell = graph.getCellById?.(lock.objectId);
						const rect = cell
							? graph.findViewByCell?.(cell)?.container?.getBoundingClientRect?.()
							: null;
						if (!rect) return lock.objectId;
						return [
							lock.objectId,
							Math.round(rect.left),
							Math.round(rect.top),
							Math.round(rect.width),
							Math.round(rect.height),
						].join(':');
					})
					.join('|');
				if (sig !== lastSig.current) {
					lastSig.current = sig;
					setTransformTick(tick => tick + 1);
				}
			}
			frame = requestAnimationFrame(sample);
		};
		frame = requestAnimationFrame(sample);
		return () => cancelAnimationFrame(frame);
	}, [remoteLocks, modelerReady, viewReady, modelerRef, containerRef]);

	if (!remoteLocks.length) return null;

	const graph = modelerRef.current?.getGraph?.() as GraphLike | undefined;
	const container = containerRef.current;
	const origin = container?.getBoundingClientRect();
	if (!graph || !origin) return null;

	return (
		<div className="object-locks-overlay" aria-hidden>
			{remoteLocks.map(lock => {
				if (lock.viewId && activeViewId && lock.viewId !== activeViewId) return null;
				const cell = graph.getCellById?.(lock.objectId);
				if (!cell) return null;
				const box = measureLockBox(graph, cell, origin);
				if (!box) return null;
				const {left, top, width, height} = box;
				const color = colorForUserId(lock.userId);
				const name = lock.userName?.trim() || 'Участник';
				const label = lock.pendingPersist ? `${name} · не сохранено` : name;
				return (
					<div
						key={lock.objectId}
						className={['object-lock', lock.pendingPersist ? 'object-lock--pending' : '']
							.filter(Boolean)
							.join(' ')}
						style={{
							color,
							width,
							height,
							transform: `translate(${left}px, ${top}px)`,
						}}
					>
						<div className="object-lock__badge" style={{background: color}} title={label}>
							{lock.pendingPersist ? <span className="object-lock__dot" /> : null}
							{label}
						</div>
					</div>
				);
			})}
		</div>
	);
};

export default React.memo(ObjectLocksOverlay);
